<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import { formatCurrency } from '../utils/crmPageUtils';
import {
  houseAddressLine,
  houseDetailsLine,
  shouldRenderSuggestedHouses,
  visitorFirstName,
} from '../utils/visitorInfoPresenter';
import PropertyMatchBreakdown from './PropertyMatchBreakdown.vue';

const props = defineProps({
  houses: { type: Array, default: () => [] },
  visitorInfo: { type: Object, default: null },
  businessContext: { type: String, default: 'for_rent' },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['entry-info', 'view-listing']);

const { t } = useI18n();

const firstName = computed(
  () => visitorFirstName(props.visitorInfo) || t('LEADS.VISITOR_INFO.CLIENT')
);
const isVisible = computed(
  () =>
    props.loading ||
    shouldRenderSuggestedHouses(props.businessContext, props.houses)
);
const thumbnailSrc = house => {
  const thumbnail = house?.thumbnail;
  if (!thumbnail) return '';
  if (typeof thumbnail === 'string') return thumbnail;
  return thumbnail.url || thumbnail.src || thumbnail.path || '';
};
const rentPrice = house => formatCurrency(house?.rent_price || house?.rent);
</script>

<template>
  <div>
    <LaisCard
      v-if="isVisible"
      :title="
        t('LEADS.VISITOR_INFO.SUGGESTED.TITLE', {
          name: firstName,
        })
      "
      prefix-icon="i-lucide-home"
      density="tight"
    >
      <div v-if="loading" class="grid gap-3 sm:grid-cols-2">
        <span class="h-28 w-full animate-pulse rounded bg-lais-bg-muted" />
        <span class="h-28 w-full animate-pulse rounded bg-lais-bg-muted" />
      </div>
      <div v-else class="grid gap-3 sm:grid-cols-2">
        <article
          v-for="house in houses"
          :key="house.id || house.house_id"
          class="overflow-hidden rounded-lais-sm border border-lais-border-soft bg-white"
        >
          <div class="flex min-h-[112px] gap-3 p-3">
            <img
              v-if="thumbnailSrc(house)"
              :src="thumbnailSrc(house)"
              :alt="house.title || houseAddressLine(house)"
              class="h-20 w-28 shrink-0 rounded-lais-sm object-cover"
            />
            <div
              v-else
              class="grid h-20 w-28 shrink-0 place-items-center rounded-lais-sm bg-lais-bg-muted text-lais-text-muted"
            >
              <span class="i-lucide-image size-5" aria-hidden="true" />
            </div>
            <div class="min-w-0 flex-1">
              <p class="font-semibold text-lais-text">
                {{
                  rentPrice(house) || t('LEADS.VISITOR_INFO.SUGGESTED.NO_PRICE')
                }}
              </p>
              <p
                class="mt-1 line-clamp-2 text-lais-body-sm text-lais-text-muted"
              >
                {{ houseAddressLine(house) }}
              </p>
              <p class="mt-1 truncate text-lais-body-sm text-lais-text-muted">
                {{ houseDetailsLine(house) }}
              </p>
              <PropertyMatchBreakdown :house="house" compact class="mt-3" />
            </div>
          </div>
          <div
            class="flex items-center justify-between gap-2 border-t border-lais-border-soft px-3 py-2"
          >
            <button
              type="button"
              class="text-lais-label-sm font-semibold text-lais-primary hover:text-lais-primary-600"
              @click="emit('entry-info', house)"
            >
              {{ t('LEADS.VISITOR_INFO.SUGGESTED.ENTRY_CONDITIONS') }}
            </button>
            <button
              type="button"
              class="grid size-8 place-items-center rounded-full text-lais-text-muted hover:bg-lais-primary-50 hover:text-lais-primary"
              :aria-label="t('LEADS.VISITOR_INFO.SUGGESTED.VIEW_LISTING')"
              :title="t('LEADS.VISITOR_INFO.SUGGESTED.VIEW_LISTING')"
              @click="emit('view-listing', house)"
            >
              <span class="i-lucide-external-link size-4" aria-hidden="true" />
            </button>
          </div>
        </article>
      </div>
    </LaisCard>
  </div>
</template>
